import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { usePostOffersCargosReject } from "@/entities/post-offers-cargos-reject/fetch";
import { usePutOffersCargo } from "@/entities/put-offers-cargo/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useSuspendOffersProcess } from "@/entities/suspend-offers-process/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import type { ShipperCargoCreatePageProps } from "@/pages/shipper-cargo-create/model";
import { excludeNull } from "@/shared/lib/collection";
import { fetchFileFromUrl } from "@/shared/lib/file";
import { isSuccessStatusCode } from "@/shared/lib/http";
import type { Loadable } from "@/shared/lib/loadable";
import { safeParseInt } from "@/shared/lib/number";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { useNavigate } from "@tanstack/react-router";
import type { FreightMatching } from "@x-mile/api-contract";
import { useCallback, useMemo, useState } from "react";
import type { ShipperCargoDetailPageProps } from "../model";

export const useShipperCargoDetailPageProps = (
  offerCargoIdStr: string,
): ShipperCargoDetailPageProps => {
  const offerCargoId = Number.parseInt(offerCargoIdStr);
  if (Number.isNaN(offerCargoId)) {
    throw new Error("荷物IDが正しくありません");
  }

  // Local Storage
  // コピーして新規作成する際に、元になる荷物情報を受け渡すためにLocalStorageに保存する
  const [_, setShipperCargoCreateInProgressValues] =
    useInProgressValuesLocalCache<
      ShipperCargoCreatePageProps["defaultInputValues"]
    >("ShipperCargoCreatePage");

  // Backend API
  const _draftOfferCargoResult = useGetOffersCargo(
    {
      id: offerCargoId,
    },
    {
      enabled: offerCargoIdStr !== undefined,
    },
  );
  // stage が draft でない場合 error に変換する
  const draftOfferCargoResult = useMemo(() => {
    if (_draftOfferCargoResult.status !== "success") {
      return _draftOfferCargoResult;
    }
    if (_draftOfferCargoResult.data.data.stage !== "draft") {
      return {
        status: "error",
        error: { message: "荷物が見つかりません" },
      } as const;
    }
    return _draftOfferCargoResult;
  }, [_draftOfferCargoResult]);

  const getDraftOfferCargo = useMemo<
    Loadable<FreightMatching.schemas.OfferCargo>
  >(() => {
    if (draftOfferCargoResult.status === "pending") {
      return {
        status: "loading",
        data: undefined,
      };
    } else if (draftOfferCargoResult.status === "error") {
      return {
        status: "error",
        error: { message: JSON.stringify(draftOfferCargoResult.error) },
      };
    }

    return {
      status: "success",
      data: draftOfferCargoResult.data.data,
    };
  }, [draftOfferCargoResult]);

  const postOfferCargeRejectMutation = usePostOffersCargosReject();

  const putOffersCargos = usePutOffersCargo();

  const postSuspendOffersProcessMutation = useSuspendOffersProcess();

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const { offerCargoProcess, refetch: refetchOfferCargoProcess } =
    fetchOfferCargoProcess(getDraftOfferCargo.data?.process.id);

  // Actions
  const navigate = useNavigate();
  const onCopyCargoClick = useCallback(() => {
    if (offerCargoProcess.status !== "success") {
      return;
    }
    setShipperCargoCreateInProgressValues(
      replaceNullWithUndefined({
        ...offerCargoProcess.data.draftOfferCargo,
        // 積み日/卸し日/受付期限は空欄とする
        loadingOn: undefined,
        unloadingOn: undefined,
        deadlineAt: undefined,
        destinations: offerCargoProcess.data.draftOfferCargo.destinations.map(
          (d) => {
            const [city, address_line] = d.address.split(" ");
            return { ...d, city, address_line };
          },
        ),
        // workaround: vehicleWeight が string で帰ってくるので変換する
        vehicleWeight:
          safeParseInt(offerCargoProcess.data.draftOfferCargo.vehicleWeight) ??
          undefined,
      }),
    );
    navigate({
      to: "/shipper/cargos/create",
    });
  }, [
    navigate,
    setShipperCargoCreateInProgressValues,
    offerCargoProcess.status,
    offerCargoProcess.data,
  ]);
  const onDeliveryProposalReject: ShipperCargoDetailPageProps["onDeliveryProposalReject"] =
    useCallback(
      (deliveryProposalOfferCargoIdToReject, params) => {
        void (async () => {
          const result = await postOfferCargeRejectMutation.mutateAsync({
            id: deliveryProposalOfferCargoIdToReject.toString(),
            data: params,
          });
          if (
            result &&
            typeof result === "object" &&
            "status" in result &&
            isSuccessStatusCode(result.status)
          ) {
            setGlobalNotification({
              variant: "success",
              message: "配送提案を見送りしました",
            });
            await _draftOfferCargoResult.refetch();
            await refetchOfferCargoProcess();
          } else {
            setGlobalNotification({
              variant: "error",
              message: `配送提案の見送りに失敗しました\n${JSON.stringify(result && typeof result === "object" && "data" in result ? result.data : result)}`,
            });
          }
        })();
      },
      [
        postOfferCargeRejectMutation,
        _draftOfferCargoResult,
        setGlobalNotification,
        refetchOfferCargoProcess,
      ],
    );
  const onProcessSuspend: ShipperCargoDetailPageProps["onProcessSuspend"] =
    useCallback(() => {
      if (offerCargoProcess.status !== "success") {
        return;
      }
      const process = offerCargoProcess.data.draftOfferCargo.process;
      if (process.isSuspended) {
        console.error("すでにこの荷物は募集停止しています");
        return;
      }
      void (async () => {
        const result = await postSuspendOffersProcessMutation.mutateAsync({
          id: process.id,
        });
        if (isSuccessStatusCode(result.status)) {
          setGlobalNotification({
            variant: "success",
            message: "荷物の募集を停止しました",
          });
          await _draftOfferCargoResult.refetch();
          await refetchOfferCargoProcess();
        } else {
          console.error(result);
          setGlobalNotification({
            variant: "error",
            message: `荷物の募集停止に失敗しました\n${JSON.stringify(
              result.data,
            )}`,
          });
        }
      })();
    }, [
      offerCargoProcess.status,
      offerCargoProcess.data,
      _draftOfferCargoResult,
      postSuspendOffersProcessMutation,
      setGlobalNotification,
      refetchOfferCargoProcess,
    ]);
  const [isDeliveryRequestFilesUploading, setIsDeliveryRequestFilesUploading] =
    useState(false);

  // 配送依頼書のファイル内容を保持するstate
  const [deliveryRequestFileContents, setDeliveryRequestFileContents] =
    useState<Map<string, File>>(new Map());

  // 契約合意済みの荷物の配送依頼書を事前にダウンロード
  useLoggingEffect(
    () => {
      if (offerCargoProcess.status === "success") {
        const contractConcludedOfferCargoData =
          offerCargoProcess.data.contractOfferCargos.find(
            (offerCargo) => offerCargo.stage === "contract_concluded",
          );

        if (contractConcludedOfferCargoData?.attachedDeliveryRequestFiles) {
          void (async () => {
            const contents = new Map<string, File>();
            const files =
              contractConcludedOfferCargoData.attachedDeliveryRequestFiles ??
              [];
            const downloadPromises = files
              .filter((file) => file.url && file.filename)
              .map(async (file) => {
                if (!file.url || !file.filename) return;
                const result = await fetchFileFromUrl(file.url, file.filename);
                if (result.isOk()) {
                  contents.set(file.url, result.value);
                }
              });

            await Promise.all(downloadPromises);
            setDeliveryRequestFileContents(contents);
          })();
        }
      }
    },
    [offerCargoProcess.status, offerCargoProcess.data?.contractOfferCargos],
    "配送依頼書のダウンロード",
  );

  const onDeliveryRequestFilesSubmit: ShipperCargoDetailPageProps["onDeliveryRequestFilesSubmit"] =
    useCallback(
      (files) => {
        if (offerCargoProcess.status !== "success") {
          return;
        }
        const contractConcludedOfferCargoData =
          offerCargoProcess.data.contractOfferCargos.find(
            (offerCargo) => offerCargo.stage === "contract_concluded",
          );
        if (!contractConcludedOfferCargoData) {
          console.error(
            `まだ契約合意に至っていません。 draftOfferCargoId: ${offerCargoId}`,
          );
          return;
        }
        void (async () => {
          setIsDeliveryRequestFilesUploading(true);
          try {
            // キャッシュから既存ファイルを取得
            const existingFiles = excludeNull(
              contractConcludedOfferCargoData.attachedDeliveryRequestFiles?.map(
                (file) => {
                  if (!file.url) {
                    return undefined;
                  }
                  return deliveryRequestFileContents.get(file.url);
                },
              ) ?? [],
            );

            if (
              contractConcludedOfferCargoData.attachedDeliveryRequestFiles &&
              existingFiles.length !==
                contractConcludedOfferCargoData.attachedDeliveryRequestFiles.filter(
                  (f) => f.url,
                ).length
            ) {
              setGlobalNotification({
                variant: "error",
                message:
                  "配送依頼書のアップロードに失敗しました。ページをリロードしてもう一度お試しください。",
              });
              return;
            }
            const result = await putOffersCargos.mutateAsync({
              id: contractConcludedOfferCargoData.id,
              data: {
                ...contractConcludedOfferCargoData,
                vehicleId: contractConcludedOfferCargoData.vehicle?.id ?? null,
                deliveryRequestFilesToAdd: files,
              },
            });
            if (isSuccessStatusCode(result.status)) {
              setGlobalNotification({
                variant: "success",
                message: "配送依頼書をアップロードしました。",
              });
              await _draftOfferCargoResult.refetch();
              await refetchOfferCargoProcess();
            } else {
              console.error(result);
              setGlobalNotification({
                variant: "error",
                message:
                  "配送依頼書のアップロードに失敗しました。ページをリロードしてもう一度お試しください。",
              });
            }
          } finally {
            setIsDeliveryRequestFilesUploading(false);
          }
        })();
      },
      [
        offerCargoProcess.status,
        offerCargoProcess.data,
        putOffersCargos,
        offerCargoId,
        _draftOfferCargoResult,
        setGlobalNotification,
        refetchOfferCargoProcess,
        deliveryRequestFileContents,
      ],
    );
  const onDeliveryRequestFileDelete: ShipperCargoDetailPageProps["onDeliveryRequestFileDelete"] =
    useCallback(
      (fileUrlToDelete) => {
        if (offerCargoProcess.status !== "success") {
          return;
        }
        const contractConcludedOfferCargoData =
          offerCargoProcess.data.contractOfferCargos.find(
            (offerCargo) => offerCargo.stage === "contract_concluded",
          );
        if (!contractConcludedOfferCargoData) {
          console.error(
            `まだ契約合意に至っていません。 draftOfferCargoId: ${offerCargoId}`,
          );
          return;
        }
        void (async () => {
          // 削除対象ファイルのIDを取得
          const fileToDelete =
            contractConcludedOfferCargoData.attachedDeliveryRequestFiles?.find(
              (file) => file.url === fileUrlToDelete,
            );

          if (!fileToDelete?.id) {
            setGlobalNotification({
              variant: "error",
              message:
                "配送依頼書の削除に失敗しました。ページをリロードしてもう一度お試しください。",
            });
            return;
          }

          const result = await putOffersCargos.mutateAsync({
            id: contractConcludedOfferCargoData.id,
            data: {
              ...contractConcludedOfferCargoData,
              vehicleId: contractConcludedOfferCargoData.vehicle?.id ?? null,
              deliveryRequestFileIdsToRemove: [fileToDelete.id],
            },
          });
          if (isSuccessStatusCode(result.status)) {
            setGlobalNotification({
              variant: "success",
              message: "配送依頼書を削除しました。",
            });
            await _draftOfferCargoResult.refetch();
            await refetchOfferCargoProcess();
          } else {
            console.error(result);
            setGlobalNotification({
              variant: "error",
              message:
                "配送依頼書の削除に失敗しました。ページをリロードしてもう一度お試しください。",
            });
          }
        })();
      },
      [
        offerCargoProcess.status,
        offerCargoProcess.data,
        putOffersCargos,
        offerCargoId,
        _draftOfferCargoResult,
        setGlobalNotification,
        refetchOfferCargoProcess,
      ],
    );
  const onRecreateContractClick: ShipperCargoDetailPageProps["onRecreateContractClick"] =
    useCallback(() => {
      if (offerCargoProcess.status !== "success") {
        return;
      }
      const latestRejectedContractProposalOfferCargo =
        offerCargoProcess.data.contractOfferCargos
          // TODO: APIにrejection.created_atがついたらソートして最後にrejectされたものを取得する
          .find(
            (offerCargo) =>
              offerCargo.stage === "contract_proposal" &&
              !!offerCargo.rejection,
          );
      if (!latestRejectedContractProposalOfferCargo) {
        console.error(
          `見送られている契約がありません draftOfferCargoId: ${offerCargoId}`,
        );
        return;
      }
      const deliveryProposalOfferCargo =
        offerCargoProcess.data.deliveryProposalOfferCargos.find(
          (offerCargo) =>
            offerCargo.id ===
            latestRejectedContractProposalOfferCargo.sourceOfferCargoId,
        );
      if (!deliveryProposalOfferCargo) {
        console.error(
          `配送提案が見つかりません deliveryProposalOfferCargoId: ${latestRejectedContractProposalOfferCargo.sourceOfferCargoId}`,
        );
        return;
      }
      setShipperCargoCreateInProgressValues(
        replaceNullWithUndefined({
          ...latestRejectedContractProposalOfferCargo,
          destinations:
            latestRejectedContractProposalOfferCargo.destinations.map((d) => {
              const [addressCity, addressChome] = d.address.split(" ");
              return { ...d, addressCity, addressChome };
            }),
          // workaround: vehicleWeight が string で帰ってくるので変換する
          vehicleWeight:
            safeParseInt(
              latestRejectedContractProposalOfferCargo.vehicleWeight,
            ) ?? undefined,
        }),
      );
      navigate({
        to: "/shipper/delivery-proposals/$id/contracts/create",
        params: { id: deliveryProposalOfferCargo.id.toString() },
      });
    }, [
      navigate,
      setShipperCargoCreateInProgressValues,
      offerCargoProcess.status,
      offerCargoProcess.data,
      offerCargoId,
    ]);

  return {
    offerCargoProcess,
    onCopyCargoClick,
    onDeliveryProposalReject,
    onProcessSuspend,
    onDeliveryRequestFilesSubmit,
    onDeliveryRequestFileDelete,
    onRecreateContractClick,
    isDeliveryRequestFilesUploading,
  };
};
